/**
 * NotificationsManager — Componente invisible que centraliza la "presencia" de
 * notificaciones en la app.
 *
 * Responsabilidades:
 *   1. Poller central de /api/notifications cada 20s (cuando tab visible).
 *   2. Detecta notifs NUEVAS desde el último poll (LS guarda último ID visto).
 *   3. Para cada notif nueva dispara:
 *      - Toast in-tab (siempre, configurable)
 *      - Sonido (si tipo crítico y prefs lo permiten)
 *      - Desktop notification (si tipo crítico y permiso granted)
 *   4. Actualiza document.title con count `(N) SOA 10 PRO`.
 *   5. Pinta dot rojo en favicon vía canvas.
 *
 * Se monta una sola vez en Layout. No renderiza nada visible.
 */

import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useToast } from "./Toast.jsx";
import { loadPrefs, shouldPlaySound, shouldShowDesktop } from "../lib/notif-prefs.js";

const POLL_INTERVAL = 20_000;
const LAST_SEEN_KEY = "crew_notif_last_seen_id";
const ORIGINAL_TITLE = "SOA 10 PRO";

/* ─── Helpers visuales ───────────────────────────────────────────────────── */

let originalFaviconHref = null;

function updateTitle(count) {
  const base = ORIGINAL_TITLE;
  document.title = count > 0 ? `(${count}) ${base}` : base;
}

function paintFavicon(hasUnread) {
  try {
    const link = document.querySelector("link[rel='icon'], link[rel='shortcut icon']");
    if (!link) return;
    if (originalFaviconHref === null) originalFaviconHref = link.href;
    if (!hasUnread) {
      link.href = originalFaviconHref;
      return;
    }
    // Pintar canvas: ícono base + punto rojo en esquina sup-derecha
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const sz = 64;
      const canvas = document.createElement("canvas");
      canvas.width = sz; canvas.height = sz;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, sz, sz);
      // Dot rojo
      ctx.fillStyle = "#ef4444";
      ctx.beginPath();
      ctx.arc(sz - 14, 14, 14, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 4;
      ctx.stroke();
      link.href = canvas.toDataURL("image/png");
    };
    img.onerror = () => {};
    img.src = originalFaviconHref;
  } catch (e) {
    // Falla silenciosa, no es crítico
  }
}

/* ─── Sonido sintetizado vía Web Audio API ───────────────────────────────── */

let audioCtx = null;
function playBeep(tipo) {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    // Si el contexto está suspendido (autoplay policy), no podemos sonar todavía
    if (audioCtx.state === "suspended") return;
    const now = audioCtx.currentTime;
    // Dos tonos para menciones, uno para QC
    const tones = tipo === "mention" || tipo === "mencion-chat"
      ? [880, 1320]                    // mention: A5 + E6 ascendente
      : [660];                          // QC: E5 simple
    tones.forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + i * 0.12);
      gain.gain.setValueAtTime(0, now + i * 0.12);
      gain.gain.linearRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.18);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.2);
    });
  } catch (e) {
    console.warn("[notif/sound]", e.message);
  }
}

/* ─── Desktop Notification ───────────────────────────────────────────────── */

function showDesktop(notif, onClick) {
  try {
    if (Notification?.permission !== "granted") return;
    const title = notif.titulo || "SOA 10 PRO";
    const body  = (notif.mensaje || "").slice(0, 140);
    const n = new Notification(title, {
      body,
      icon: "/favicon.png",
      tag: notif.id,         // dedupe si llega misma notif 2x
      requireInteraction: false,
    });
    n.onclick = () => {
      window.focus();
      onClick();
      n.close();
    };
  } catch (e) {
    console.warn("[notif/desktop]", e.message);
  }
}

/* ─── Build link from notif (igual lógica que NotificationsDropdown) ─────── */
function buildLink(notif) {
  if (notif.link) return notif.link;
  const tipo = notif.entidad_tipo;
  const id   = notif.entidad_id;
  if (tipo === "tarea" && id) return `/tareas?focus=${id}`;
  if (!tipo || !id) return null;
  const MAP = {
    cliente:        `/clientes/${id}`,
    factura:        `/facturacion`,
    proyecto:       `/proyectos/${id}`,
    "pedido-cliente": `/pedidos-cliente`,
    "content-plan": `/generador-contenido?focus=${id}`,
  };
  return MAP[tipo] || null;
}

/* ─── Componente principal ───────────────────────────────────────────────── */

export function NotificationsManager() {
  const { miembro } = useAuth();
  const userId = miembro?.id;
  const { toast } = useToast();
  const navigate = useNavigate();

  const lastSeenIdRef = useRef(null);
  const prefsRef      = useRef(loadPrefs());
  const isFirstLoadRef = useRef(true);

  // Cargar último ID visto desde LS
  useEffect(() => {
    if (!userId) return;
    const key = `${LAST_SEEN_KEY}_${userId}`;
    lastSeenIdRef.current = localStorage.getItem(key) || null;
  }, [userId]);

  // Refrescar prefs si cambian en otra tab
  useEffect(() => {
    const onChange = (e) => { prefsRef.current = e.detail || loadPrefs(); };
    window.addEventListener("crew-notif-prefs-changed", onChange);
    const onStorage = () => { prefsRef.current = loadPrefs(); };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("crew-notif-prefs-changed", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const poll = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.getNotifications({ user_id: userId, unread_only: "true", limit: 30 });
      const notifs = res.notifications ?? [];
      const unreadCount = res.unread_count ?? notifs.length;

      // Actualizar title + favicon (siempre)
      const prefs = prefsRef.current;
      if (prefs.titleBar) updateTitle(unreadCount);
      if (prefs.favicon)  paintFavicon(unreadCount > 0);

      // Detectar nuevas (las que vienen DESPUÉS del último ID visto)
      if (notifs.length === 0) {
        // Limpiar lastSeen si ya no hay nada nuevo
        return;
      }

      // En el primer load no disparamos nada (para no saturar al entrar)
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        lastSeenIdRef.current = notifs[0].id;
        if (userId) localStorage.setItem(`${LAST_SEEN_KEY}_${userId}`, notifs[0].id);
        return;
      }

      // Identificar las nuevas (asumiendo orden por created_at desc)
      const lastSeen = lastSeenIdRef.current;
      const nuevas = [];
      for (const n of notifs) {
        if (n.id === lastSeen) break;
        nuevas.push(n);
      }

      // v6.5 · Anti-spam: si llegan ≥3 notificaciones de baja prioridad
      // simultáneamente, las consolidamos en 1 solo toast.
      // Las "alta prioridad" siempre van individuales (mention, apelacion, qc).
      const ALTA_PRIO = new Set([
        "mention", "mencion-chat", "apelacion-pendiente", "apelacion-decidida",
        "apelacion-escalada", "apelacion-recordatorio", "qc-pendiente",
      ]);
      const altas = nuevas.filter(n => ALTA_PRIO.has(n.tipo) || n.prioridad === "alta");
      const bajas = nuevas.filter(n => !ALTA_PRIO.has(n.tipo) && n.prioridad !== "alta");

      // Disparar UX individuales SOLO para alta prioridad
      for (const n of altas) {
        const link = buildLink(n);
        const onClick = () => link && navigate(link);

        if (prefs.toast) {
          toast.info?.(n.titulo || "Nueva notificación", {
            description: (n.mensaje || "").slice(0, 100),
            duration: 6000,
            action: link ? { label: "Abrir", onClick } : undefined,
          });
        }
        if (shouldPlaySound(n.tipo, prefs)) playBeep(n.tipo);
        if (document.hidden && shouldShowDesktop(n.tipo, prefs)) showDesktop(n, onClick);
      }

      // Consolidar bajas: si ≥3, agruparlas en 1 toast
      if (prefs.toast && bajas.length >= 3) {
        toast.info?.(`${bajas.length} actualizaciones nuevas`, {
          description: bajas.slice(0, 3).map(n => `· ${(n.titulo || "").slice(0, 50)}`).join("\n"),
          duration: 5000,
          action: { label: "Ver todas", onClick: () => navigate("/notificaciones") },
        });
      } else if (prefs.toast) {
        for (const n of bajas) {
          const link = buildLink(n);
          const onClick = () => link && navigate(link);
          toast.info?.(n.titulo || "Nueva notificación", {
            description: (n.mensaje || "").slice(0, 100),
            duration: 4000,
            action: link ? { label: "Abrir", onClick } : undefined,
          });
        }
      }

      // Actualizar lastSeen
      if (nuevas.length > 0) {
        lastSeenIdRef.current = notifs[0].id;
        if (userId) localStorage.setItem(`${LAST_SEEN_KEY}_${userId}`, notifs[0].id);
      }
    } catch (e) {
      // Silenciar errores de polling (red, auth expirada, etc.)
    }
  }, [userId, navigate, toast]);

  useEffect(() => {
    if (!userId) {
      updateTitle(0);
      paintFavicon(false);
      return;
    }
    poll();
    const id = setInterval(() => { if (!document.hidden) poll(); }, POLL_INTERVAL);
    const onVis = () => { if (!document.hidden) poll(); };
    const onFocus = () => poll();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    // Resume audio context en primera interacción del usuario (browser autoplay policy)
    const resumeAudio = () => {
      try { audioCtx?.resume?.(); } catch {}
      document.removeEventListener("click", resumeAudio);
      document.removeEventListener("keydown", resumeAudio);
    };
    document.addEventListener("click", resumeAudio);
    document.addEventListener("keydown", resumeAudio);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("click", resumeAudio);
      document.removeEventListener("keydown", resumeAudio);
    };
  }, [userId, poll]);

  return null;
}
